import { ChevronLeft } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { InputWithLabel } from "./components/input-label";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import BackNavigator from "./components/back-navigation";
import { useForm } from "@tanstack/react-form";
import { resetPageSchema } from "@/lib/form-schema";
import { cn, describeMissingFields } from "@/lib/utils";
import type { IAccountResetResponse } from "@/hooks/use-account-reset";
import { useForgotUsername, usePasswordReset } from "@/hooks/use-account-reset";
import { useGroupProvider } from "@/context/group-provider";
import ScreenRouteNames from "@/constants/screen-route-names";
import IDENTIFIERS from "@/constants/test_id";
import { LogSubCategories } from "@/services/logger/logger-types";

const ForgetPassword = () => {
  const navigate = useNavigate();
  const { groupData } = useGroupProvider();
  const resetQuery = usePasswordReset();
  const forgotUsernameQuery = useForgotUsername();

  const forgetUsernameForm = useForm({
    defaultValues: {
      email: "",
    },
    onSubmit: ({ value }) => {
      const userInfo = {
        email: value.email,
        groupId: groupData!.group_id,
        loginGroupId: groupData!.entered_id,
      };
      forgotUsernameQuery.mutate(userInfo, {
        onSuccess: (data: IAccountResetResponse) => {
          if (!data.success) return;
          navigate(`/${ScreenRouteNames.LOGIN.path}`);
        },
      });
    },
  });
  const forgetPasswordForm = useForm({
    defaultValues: {
      username: "",
    },
    onSubmit: ({ value }) => {
      const userInfo = {
        username: value.username,
        groupId: groupData!.group_id,
        loginGroupId: groupData!.entered_id,
        divisionId: groupData!.division_id,
      };
      resetQuery.mutate(userInfo, {
        onSuccess: (data: IAccountResetResponse) => {
          if (!data.success) return;
          navigate(`/${ScreenRouteNames.VERIFICATION.path}`, {
            state: {
              userInfo,
              signature: data.signature,
            },
          });
        },
      });
    },
  });

  return (
    <main className="flex h-full flex-col items-center justify-center space-y-10 px-8">
      <BackNavigator
        title="Account Reset"
        left={
          <Button
            variant="ghost"
            aria-label="Go back"
            className="left-0 h-auto w-fit bg-transparent p-0 hover:bg-transparent hover:shadow-none focus:bg-transparent focus:shadow-none active:bg-transparent"
            onClick={() => {
              if (window.history.length > 1) {
                navigate(-1);
              } else {
                navigate("/nav-error");
              }
            }}
            data-testid={IDENTIFIERS.ACCOUNT_RESET.BACK_BUTTON}
          >
            <ChevronLeft size={20} />
          </Button>
        }
      />

      {/*
        4.1.3 Status Messages — AA. Both forms submit to the network but neither announced
        anything: the password form only swapped its button label (unreliable, since the
        button is disabled at the same moment) and the username form gave no feedback at
        all. The region is mounted unconditionally so its text change is announced.
      */}
      <div role="status" aria-live="polite" className="sr-only">
        {resetQuery.isPending
          ? "Verifying your username"
          : forgotUsernameQuery.isPending
            ? "Verifying your email address"
            : ""}
      </div>

      <Tabs
        defaultValue="password"
        className="w-full"
        logMetaByValue={{
          password: { subCategory: LogSubCategories.FORGOT_PASSWORD },
          username: { subCategory: LogSubCategories.FORGOT_USERNAME },
        }}
      >
        {/* 4.1.2 Name, Role, Value — A: a tablist with no visible heading naming it needs
            an accessible name, or a screen reader announces a bare "tab list". */}
        <TabsList
          aria-label="Account reset options"
          className="grid w-full grid-cols-2"
        >
          <TabsTrigger
            value="password"
            data-testid={IDENTIFIERS.ACCOUNT_RESET.FORGET_PASSWORD_TAB}
          >
            Forgot Password
          </TabsTrigger>
          <TabsTrigger
            value="username"
            data-testid={IDENTIFIERS.ACCOUNT_RESET.FORGET_USERNAME_TAB}
          >
            Forgot Username
          </TabsTrigger>
        </TabsList>

        <TabsContent value="password">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              forgetPasswordForm.handleSubmit();
            }}
          >
            <Card>
              <CardHeader>
                <CardTitle as="h2">Recover Password</CardTitle>
                <CardDescription>
                  We need your username to recover your password.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <forgetPasswordForm.Field
                  name="username"
                  validators={{
                    onBlur: ({ value }) => {
                      const result =
                        resetPageSchema.shape.userName.safeParse(value);
                      return result.success
                        ? undefined
                        : result.error.errors[0].message;
                    },
                  }}
                >
                  {(field) => (
                    <div className="flex flex-row items-center space-x-2">
                      <InputWithLabel
                        id="username"
                        // 3.3.2 Labels or Instructions — A: was commented out, leaving the
                        // placeholder as the only cue — and it disappears on first keystroke.
                        label="Username"
                        placeholder="Username"
                        // 1.3.5 Identify Input Purpose — AA
                        autoComplete="username"
                        value={field.state.value}
                        onChange={(newValue) => field.handleChange(newValue)}
                        onBlur={() => field.handleBlur()}
                        error={field.state.meta.errors[0]}
                        data-testid={IDENTIFIERS.ACCOUNT_RESET.USERNAME_INPUT}
                      />
                    </div>
                  )}
                </forgetPasswordForm.Field>
              </CardContent>
              <CardFooter>
                <forgetPasswordForm.Subscribe
                  selector={(state) => [state.canSubmit, state.values.username]}
                >
                  {([canSubmit, username]) => {
                    // 3.3.1 Error Identification — A: names the specific empty
                    // field(s) rather than a static list.
                    const requirement =
                      describeMissingFields([
                        { label: "username", value: username },
                      ]) || "Disabled. Fix the errors above to continue.";
                    return (
                      <>
                        <Button
                          className={cn(
                            "w-2/3 bg-primary hover:bg-primary-selected",
                            !canSubmit && "opacity-50",
                          )}
                          type="submit"
                          variant="default"
                          disabled={resetQuery.isPending}
                          aria-disabled={!canSubmit}
                          aria-describedby={
                            !canSubmit
                              ? "forgot-password-verify-requirement"
                              : undefined
                          }
                          data-testid={
                            IDENTIFIERS.ACCOUNT_RESET
                              .FORGOT_PASSWORD_VERIFY_BUTTON
                          }
                        >
                          {resetQuery.isPending ? "Verifying..." : "Verify"}
                        </Button>
                        {!canSubmit && (
                          <p
                            id="forgot-password-verify-requirement"
                            className="sr-only"
                          >
                            {requirement}
                          </p>
                        )}
                      </>
                    );
                  }}
                </forgetPasswordForm.Subscribe>
              </CardFooter>
            </Card>
          </form>
        </TabsContent>
        <TabsContent value="username">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              forgetUsernameForm.handleSubmit();
            }}
          >
            <Card>
              <CardHeader>
                <CardTitle as="h2">Recover Username</CardTitle>
                <CardDescription>
                  We need your email address to recover your username.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <forgetUsernameForm.Field
                  name="email"
                  validators={{
                    onBlur: ({ value }) => {
                      const result =
                        resetPageSchema.shape.email.safeParse(value);
                      return result.success
                        ? undefined
                        : result.error.errors[0].message;
                    },
                  }}
                >
                  {(field) => (
                    <div className="flex flex-row items-center space-x-2">
                      <InputWithLabel
                        id="email"
                        // 3.3.2 Labels or Instructions — A: was commented out.
                        label="Email"
                        placeholder="Email"
                        // 1.3.5 Identify Input Purpose — AA
                        autoComplete="email"
                        value={field.state.value}
                        onChange={(newValue) => field.handleChange(newValue)}
                        onBlur={() => field.handleBlur()}
                        error={field.state.meta.errors[0]}
                        data-testid={IDENTIFIERS.ACCOUNT_RESET.EMAIL_INPUT}
                      />
                    </div>
                  )}
                </forgetUsernameForm.Field>
              </CardContent>
              <CardFooter>
                <forgetUsernameForm.Subscribe
                  selector={(state) => [state.canSubmit, state.values.email]}
                >
                  {([canSubmit, email]) => {
                    // Same fix as the forgot-password Verify button above.
                    const requirement =
                      describeMissingFields([
                        { label: "email", value: email },
                      ]) || "Disabled. Fix the errors above to continue.";
                    return (
                      <>
                        <Button
                          className={cn(
                            "w-2/3 bg-primary hover:bg-primary-selected",
                            !canSubmit && "opacity-50",
                          )}
                          type="submit"
                          variant="default"
                          aria-disabled={!canSubmit}
                          aria-describedby={
                            !canSubmit
                              ? "forgot-username-verify-requirement"
                              : undefined
                          }
                          data-testid={
                            IDENTIFIERS.ACCOUNT_RESET
                              .FORGOT_USERNAME_VERIFY_BUTTON
                          }
                        >
                          Verify
                        </Button>
                        {!canSubmit && (
                          <p
                            id="forgot-username-verify-requirement"
                            className="sr-only"
                          >
                            {requirement}
                          </p>
                        )}
                      </>
                    );
                  }}
                </forgetUsernameForm.Subscribe>
              </CardFooter>
            </Card>
          </form>
        </TabsContent>
      </Tabs>
    </main>
  );
};

export default ForgetPassword;
